import { createFurniturePicker } from './furniturePick.js';

/**
 * CG の物件で、邪魔な家具をクリックして消す。
 *
 * スキャンの物件は部屋全体が1つの面のかたまりなので、家具だけを外せない。
 * CG は家具ひとつひとつが別の部品（buildHouse.js が userData.furniture を付けている）なので、
 * クリックされた部品をたどって、その家具ごと非表示にできる。
 *
 * 消した家具は物件ごとにブラウザへ覚えさせ、次に開いたときも消えたままにする。
 * 家具は歩行の当たり判定にも入っているため、消したあとは当たり判定を作り直す（onChange）。
 */
const STORAGE_PREFIX = 'naiken:removed-furniture:';

export function createFurnitureRemover({ root, camera, canvas, propertyId, onChange }) {
  const picker = createFurniturePicker({ root, camera, canvas });
  const items = picker.items;
  if (items.size === 0) return null;

  let enabled = false;
  let removed = read(propertyId);
  let onPick = null;

  /** 覚えていた分を最初から消しておく */
  const apply = () => {
    for (const [index, node] of items) node.visible = !removed.has(index);
  };
  apply();

  const pick = (event) => picker.pick(event);

  const onClick = (event) => {
    if (!enabled) return;
    const node = pick(event);
    if (node === null) return;
    event.preventDefault();
    event.stopPropagation();
    const { index, label } = node.userData.furniture;
    removed = new Set([...removed, index]);
    node.visible = false;
    write(propertyId, removed);
    onChange?.({ count: removed.size, label });
    onPick?.(label);
  };

  // クリックで視点が動かないよう、押した時点で先に止める
  const onPointerDown = (event) => {
    if (!enabled) return;
    if (pick(event) !== null) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  canvas.addEventListener('pointerdown', onPointerDown, { capture: true });
  canvas.addEventListener('click', onClick, { capture: true });

  return {
    /** 消すモードの入・切 */
    setEnabled(next) {
      enabled = Boolean(next);
      canvas.style.cursor = enabled ? 'pointer' : '';
    },
    get enabled() {
      return enabled;
    },
    get count() {
      return removed.size;
    },
    /** 消した家具を全部戻す */
    restoreAll() {
      removed = new Set();
      write(propertyId, removed);
      apply();
      onChange?.({ count: 0, label: null });
    },
    /** 家具を消したときに名前を受け取る（画面の案内用） */
    onRemoved(handler) {
      onPick = handler;
    },
    dispose() {
      canvas.removeEventListener('pointerdown', onPointerDown, { capture: true });
      canvas.removeEventListener('click', onClick, { capture: true });
    },
  };
}

function read(propertyId) {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_PREFIX + propertyId) ?? '[]');
    return new Set(Array.isArray(raw) ? raw.filter((n) => Number.isInteger(n)) : []);
  } catch {
    return new Set();
  }
}

function write(propertyId, removed) {
  try {
    localStorage.setItem(STORAGE_PREFIX + propertyId, JSON.stringify([...removed]));
  } catch {
    // 保存できなくても、その場で消えていれば内見はできる
  }
}
